import { useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../lib/firebase'
import { useAuth } from '../lib/auth'
import { useSkin } from '../lib/skin'
import Page from '../components/Page'

interface RepairReport {
  artistsFlagged: number
  showsGeocoded: number
  showsStillUnfound: number
  timezonesFixed: number
  showsChecked: number
  more: boolean
}

const repair = httpsCallable<{ dryRun?: boolean }, RepairReport>(
  getFunctions(app),
  'repairMyData',
)

export default function Account() {
  const { user, signOut } = useAuth()
  const { preference, setPreference } = useSkin()
  const [fixing, setFixing] = useState(false)
  const [report, setReport] = useState<RepairReport | null>(null)
  const [repairError, setRepairError] = useState<string | null>(null)

  async function runRepair() {
    setFixing(true)
    setRepairError(null)
    try {
      const response = await repair({})
      setReport(response.data)
    } catch (caught) {
      setRepairError(
        caught instanceof Error ? caught.message : 'That did not work. Try again in a moment.',
      )
    } finally {
      setFixing(false)
    }
  }

  return (
    <Page title="Account">
      <section className="section">
        <h2 className="t-eyebrow">Profile</h2>
        <div className="panel panel-pad spread">
          <div className="stack" style={{ gap: 3 }}>
            <span style={{ fontWeight: 600 }}>{user?.displayName || 'Encore user'}</span>
            <span className="t-caption">{user?.email}</span>
          </div>
          <button className="btn btn-secondary" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </section>

      <section className="section">
        <h2 className="t-eyebrow">Appearance</h2>
        <div className="panel panel-pad stack">
          <p className="t-caption">
            Showtime is the dark stage look, Daylight is for load-in mornings and bright rooms.
            Auto follows this computer.
          </p>
          <div className="row" style={{ gap: 8 }}>
            {(['system', 'daylight', 'showtime'] as const).map((option) => (
              <button
                key={option}
                className={preference === option ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={() => setPreference(option)}
              >
                {option === 'system' ? 'Auto' : option === 'daylight' ? 'Daylight' : 'Showtime'}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ repair */}
      <section className="section">
        <h2 className="t-eyebrow">Check my data</h2>
        <div className="panel panel-pad stack" style={{ gap: 12 }}>
          <p className="t-body dim">
            Fills in venue locations for shows that were imported rather than typed, and corrects
            any show whose timezone doesn&rsquo;t match where it actually is. Safe to run more than
            once &mdash; it only writes where something is missing or wrong.
          </p>

          <button
            className="btn btn-secondary"
            style={{ alignSelf: 'flex-start' }}
            disabled={fixing}
            onClick={() => void runRepair()}
          >
            {fixing ? 'Checking…' : 'Check and fix'}
          </button>

          {fixing && (
            <p className="t-caption">
              Looking venues up one at a time, so this takes a minute or two on a big account.
            </p>
          )}

          {repairError && <p className="t-caption error">{repairError}</p>}

          {report && !fixing && (
            <div className="stack" style={{ gap: 4 }}>
              <span className="t-caption">
                Checked {report.showsChecked} show{report.showsChecked === 1 ? '' : 's'}.
              </span>
              {report.showsGeocoded > 0 && (
                <span className="t-caption">
                  Located {report.showsGeocoded} venue{report.showsGeocoded === 1 ? '' : 's'}.
                </span>
              )}
              {report.timezonesFixed > 0 && (
                <span className="t-caption">
                  Corrected {report.timezonesFixed} timezone
                  {report.timezonesFixed === 1 ? '' : 's'}.
                </span>
              )}
              {report.artistsFlagged > 0 && (
                <span className="t-caption">
                  Hid {report.artistsFlagged} auto-created artist
                  {report.artistsFlagged === 1 ? '' : 's'} from the roster.
                </span>
              )}
              {report.showsStillUnfound > 0 && (
                <span className="t-caption" style={{ color: 'var(--warning)' }}>
                  {report.showsStillUnfound} venue
                  {report.showsStillUnfound === 1 ? " couldn't" : "s couldn't"} be found on a map.
                  Adding an address to those shows will fix it.
                </span>
              )}
              {report.more && (
                <span className="t-caption">
                  There&rsquo;s more to do &mdash; run it again to carry on.
                </span>
              )}
              {report.showsGeocoded === 0 &&
                report.timezonesFixed === 0 &&
                report.artistsFlagged === 0 && (
                  <span className="t-caption">Nothing needed fixing.</span>
                )}
            </div>
          )}
        </div>
      </section>
    </Page>
  )
}
